import { StateCreator } from "zustand";
import { produce } from "immer";
import posthog from "posthog-js";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { OrganizationType } from "@/lib/types/types";
import { useCentralStore } from "../Central";
import { showNotification } from "../Central/selectors";
import {
	CreatedPaymentReminder,
	InvoiceOpenReminderFee,
	PaymentReminderCandidate,
	PaymentReminderDocument,
	PaymentReminderHistoryRow,
	PaymentReminderSlipContext,
	ReminderCandidateMode,
	ReminderHistorySortColumn,
	ReminderHistoryStatusFilter,
} from "../../pages/debitoren/mahnwesen/payment-reminder.types";
import { OPEN_EPSILON } from "../../pages/debitoren/mahnwesen/utils/payment-reminder-document";

const REMINDER_ERROR_MESSAGES: Record<string, string> = {
	not_eligible: "Die Rechnung ist für diese Mahnstufe nicht (mehr) fällig.",
	forbidden: "Keine Berechtigung für das Mahnwesen.",
	void_reason_required: "Bitte geben Sie einen Grund an.",
	pause_reason_required: "Bitte geben Sie einen Grund für den Mahnstopp an.",
	pause_until_in_past: "Das Datum des Mahnstopps liegt in der Vergangenheit.",
	not_latest_stage: "Nur die letzte Mahnstufe kann zurückgenommen werden.",
	fee_paid:
		"Die Mahngebühr wurde bereits bezahlt. Die Mahnung kann nicht zurückgenommen werden.",
	already_voided: "Die Mahnung wurde bereits zurückgenommen.",
	reminder_not_found: "Die Mahnung wurde nicht gefunden.",
	invoice_not_found: "Die Rechnung wurde nicht gefunden.",
};

export function getReminderErrorMessage(error: { message: string }): string {
	const key = Object.keys(REMINDER_ERROR_MESSAGES).find((code) =>
		error.message.includes(code)
	);
	return key ? REMINDER_ERROR_MESSAGES[key] : error.message;
}

const notifyError = (error: { message: string }, context: string) => {
	Logger.error(error, {}, context);
	showNotification({
		message: getReminderErrorMessage(error),
		type: "error",
	});
};

const getOrganizationId = () => useCentralStore.getState().organization?.id;

let historyRequestId = 0;

export interface ReminderHistoryQuery {
	search: string;
	stage: number | null;
	recipientId: string | null;
	dateFrom: string | null;
	dateTo: string | null;
	sortBy: ReminderHistorySortColumn;
	sortDesc: boolean;
	status: ReminderHistoryStatusFilter | null;
	pageIndex: number;
	pageSize: number;
}

export type ReminderShareResult =
	| { reshared: true }
	| {
			reshared: false;
			email: string | null;
			organizationType: OrganizationType | null;
	  };

export interface FeeInvoiceReminder {
	stage: number;
	isLatest: boolean;
}

export type ReminderDeliveryField = "printed_at" | "downloaded_at";

export interface RemindersSlice {
	candidates: PaymentReminderCandidate[];
	candidatesMode: ReminderCandidateMode;
	candidatesLoading: boolean;
	dueCount: number;
	history: PaymentReminderHistoryRow[];
	historyTotal: number;
	historyLoading: boolean;
	fetchCandidates: (mode: ReminderCandidateMode) => Promise<void>;
	fetchDueCount: () => Promise<void>;
	fetchManualCandidate: (
		invoiceId: number
	) => Promise<PaymentReminderCandidate | null>;
	createReminder: (
		invoiceId: number,
		ignoreSchedule: boolean,
		stage: number
	) => Promise<{ data: CreatedPaymentReminder | null; error: string | null }>;
	fetchReminderDocument: (
		reminderId: number
	) => Promise<PaymentReminderDocument | null>;
	voidReminder: (
		reminderId: number,
		reason: string,
		notify?: boolean
	) => Promise<boolean>;
	pauseReminders: (
		invoiceIds: number[],
		until: string | null,
		reason: string
	) => Promise<boolean>;
	resumeReminders: (invoiceIds: number[]) => Promise<boolean>;
	fetchHistory: (query: ReminderHistoryQuery) => Promise<void>;
	fetchInvoiceHistory: (
		invoiceId: number
	) => Promise<PaymentReminderHistoryRow[]>;
	markDelivered: (
		reminderId: number,
		field: ReminderDeliveryField
	) => Promise<void>;
	shareReminder: (
		reminderId: number,
		fileId: string
	) => Promise<ReminderShareResult | null>;
	unshareReminder: (reminderId: number) => Promise<boolean>;
	fetchOpenReminderFees: (
		invoiceId: number
	) => Promise<InvoiceOpenReminderFee[]>;
	fetchFeeInvoiceReminder: (
		feeInvoiceId: number
	) => Promise<FeeInvoiceReminder | null>;
	fetchSlipContext: (
		invoiceIds: number[]
	) => Promise<PaymentReminderSlipContext[] | null>;
}

export const createRemindersStore: StateCreator<RemindersSlice> = (
	set,
	get
) => ({
	candidates: [],
	candidatesMode: "due",
	candidatesLoading: false,
	dueCount: 0,
	history: [],
	historyTotal: 0,
	historyLoading: false,

	fetchCandidates: async (mode) => {
		const organizationId = getOrganizationId();
		if (!organizationId) return;
		set(
			produce((state: RemindersSlice) => {
				if (state.candidatesMode !== mode) state.candidates = [];
				state.candidatesMode = mode;
				state.candidatesLoading = true;
			})
		);
		if (mode === "due") get().fetchDueCount();
		const { data, error } = await supabase.rpc(
			"payment_reminder_candidates",
			{ p_organization_id: organizationId, p_mode: mode }
		);
		if (get().candidatesMode !== mode) return;
		if (error) {
			notifyError(error, "Error fetching payment reminder candidates");
			set({ candidatesLoading: false });
			return;
		}
		set({ candidates: data ?? [], candidatesLoading: false });
	},

	fetchDueCount: async () => {
		const organizationId = getOrganizationId();
		if (!organizationId) return;
		const { data, error } = await supabase.rpc(
			"count_due_payment_reminders",
			{ p_organization_id: organizationId }
		);
		if (error) {
			Logger.error(error, {}, "Error counting due payment reminders");
			return;
		}
		set({ dueCount: data ?? 0 });
	},

	fetchManualCandidate: async (invoiceId) => {
		const organizationId = getOrganizationId();
		if (!organizationId) return null;
		const { data, error } = await supabase.rpc(
			"payment_reminder_candidates",
			{
				p_organization_id: organizationId,
				p_invoice_id: invoiceId,
				p_mode: "manual",
			}
		);
		if (error) {
			notifyError(error, "Error fetching manual reminder candidate");
			return null;
		}
		return data?.[0] ?? null;
	},

	createReminder: async (invoiceId, ignoreSchedule, stage) => {
		const { data, error } = await supabase.rpc("create_payment_reminder", {
			p_invoice_id: invoiceId,
			p_ignore_schedule: ignoreSchedule,
			p_stage: stage,
		});
		if (error) {
			Logger.error(error, {}, "Error creating payment reminder");
			return { data: null, error: getReminderErrorMessage(error) };
		}
		return {
			data: data as unknown as CreatedPaymentReminder,
			error: null,
		};
	},

	fetchReminderDocument: async (reminderId) => {
		const { data, error } = await supabase.rpc(
			"get_payment_reminder_document",
			{ p_reminder_id: reminderId }
		);
		if (error) {
			notifyError(error, "Error fetching payment reminder document");
			return null;
		}
		return data as unknown as PaymentReminderDocument;
	},

	voidReminder: async (reminderId, reason, notify = true) => {
		const { error } = await supabase.rpc("void_payment_reminder", {
			p_reminder_id: reminderId,
			p_reason: reason,
		});
		if (error) {
			notifyError(error, "Error voiding payment reminder");
			return false;
		}
		if (notify) {
			posthog.capture("payment_reminder_voided");
			showNotification({
				message: "Mahnung zurückgenommen",
				type: "success",
			});
		}
		get().fetchDueCount();
		return true;
	},

	pauseReminders: async (invoiceIds, until, reason) => {
		for (const invoiceId of invoiceIds) {
			const { error } = await supabase.rpc("pause_invoice_reminders", {
				p_invoice_id: invoiceId,
				p_until: until ?? undefined,
				p_reason: reason,
			});
			if (error) {
				notifyError(error, "Error pausing invoice reminders");
				get().fetchDueCount();
				return false;
			}
		}
		posthog.capture("invoice_reminders_paused", {
			count: invoiceIds.length,
			indefinite: until === null,
		});
		showNotification({
			message:
				invoiceIds.length === 1
					? "Mahnstopp gesetzt"
					: `Mahnstopp für ${invoiceIds.length} Rechnungen gesetzt`,
			type: "success",
		});
		get().fetchDueCount();
		return true;
	},

	resumeReminders: async (invoiceIds) => {
		for (const invoiceId of invoiceIds) {
			const { error } = await supabase.rpc("resume_invoice_reminders", {
				p_invoice_id: invoiceId,
			});
			if (error) {
				notifyError(error, "Error resuming invoice reminders");
				get().fetchDueCount();
				return false;
			}
		}
		posthog.capture("invoice_reminders_resumed", {
			count: invoiceIds.length,
		});
		showNotification({
			message:
				invoiceIds.length === 1
					? "Mahnstopp aufgehoben"
					: `Mahnstopp für ${invoiceIds.length} Rechnungen aufgehoben`,
			type: "success",
		});
		get().fetchDueCount();
		return true;
	},

	fetchHistory: async ({
		search,
		stage,
		recipientId,
		dateFrom,
		dateTo,
		sortBy,
		sortDesc,
		status,
		pageIndex,
		pageSize,
	}) => {
		const organizationId = getOrganizationId();
		if (!organizationId) return;
		const requestId = ++historyRequestId;
		set({ historyLoading: true });
		const { data, error } = await supabase.rpc(
			"get_payment_reminder_history",
			{
				p_organization_id: organizationId,
				p_search: search.trim() || undefined,
				p_stage: stage ?? undefined,
				p_recipient_id: recipientId ?? undefined,
				p_date_from: dateFrom ?? undefined,
				p_date_to: dateTo ?? undefined,
				p_sort_by: sortBy,
				p_sort_desc: sortDesc,
				p_status: status ?? undefined,
				p_limit: pageSize,
				p_offset: pageIndex * pageSize,
			}
		);
		if (requestId !== historyRequestId) return;
		if (error) {
			notifyError(error, "Error fetching payment reminder history");
			set({ historyLoading: false });
			return;
		}
		set({
			history: data ?? [],
			historyTotal: Number(data?.[0]?.total_count ?? 0),
			historyLoading: false,
		});
	},

	fetchInvoiceHistory: async (invoiceId) => {
		const organizationId = getOrganizationId();
		if (!organizationId) return [];
		const { data, error } = await supabase.rpc(
			"get_payment_reminder_history",
			{
				p_organization_id: organizationId,
				p_invoice_id: invoiceId,
				p_limit: 100,
			}
		);
		if (error) {
			notifyError(error, "Error fetching invoice reminder history");
			return [];
		}
		return data ?? [];
	},

	markDelivered: async (reminderId, field) => {
		const timestamp = new Date().toISOString();
		const { error } = await supabase
			.from("payment_reminders")
			.update({ [field]: timestamp })
			.eq("id", reminderId);
		if (error) {
			notifyError(error, "Error marking payment reminder delivered");
			return;
		}
		set(
			produce((state: RemindersSlice) => {
				const row = state.history.find(
					(entry) => entry.reminder_id === reminderId
				);
				if (row) row[field] = timestamp;
			})
		);
	},

	shareReminder: async (reminderId, fileId) => {
		const { data, error } = await supabase.rpc("share_payment_reminder", {
			p_reminder_id: reminderId,
			p_message_content: {
				type: "shared_file",
				file_id: fileId,
				text: "",
			},
		});
		if (error) {
			notifyError(error, "Error sharing payment reminder");
			return null;
		}
		const result = data as unknown as {
			success: boolean;
			message: string;
			reshared?: boolean;
			connected_organization_id?: string;
		};
		if (!result.success || !result.connected_organization_id) {
			showNotification({ message: result.message, type: "warning" });
			return null;
		}
		if (result.reshared) {
			showNotification({ message: "Mahnung erneut geteilt", type: "success" });
			return { reshared: true };
		}
		posthog.capture("payment_reminder_shared");
		const sharedAt = new Date().toISOString();
		set(
			produce((state: RemindersSlice) => {
				const entry = state.history.find(
					(item) => item.reminder_id === reminderId
				);
				if (entry) entry.shared_at = sharedAt;
			})
		);
		showNotification({
			message: "Mahnung über dentCONNECT geteilt",
			type: "success",
		});
		const { data: profile, error: profileError } = await supabase
			.from("profiles")
			.select("email, organization_type")
			.eq("organization_id", result.connected_organization_id)
			.order("created_at", { ascending: true })
			.limit(1)
			.maybeSingle();
		if (profileError || !profile?.email) {
			Logger.error(
				profileError,
				{},
				"Error fetching reminder recipient email"
			);
			showNotification({
				message:
					"Die Mahnung wurde geteilt, aber die E-Mail des Kunden wurde nicht gefunden.",
				type: "warning",
			});
		}
		return {
			reshared: false,
			email: profile?.email ?? null,
			organizationType:
				profile?.organization_type === "lab" ||
				profile?.organization_type === "med"
					? profile.organization_type
					: null,
		};
	},

	unshareReminder: async (reminderId) => {
		const { data, error } = await supabase.rpc("unshare_payment_reminder", {
			p_reminder_id: reminderId,
		});
		if (error) {
			notifyError(error, "Error unsharing payment reminder");
			return false;
		}
		const result = data as unknown as { success: boolean; message: string };
		if (!result.success) {
			showNotification({ message: result.message, type: "warning" });
			return false;
		}
		showNotification({
			message: "Mahnung wird nicht mehr geteilt",
			type: "success",
		});
		return true;
	},

	fetchSlipContext: async (invoiceIds) => {
		const { data, error } = await supabase.rpc(
			"get_payment_reminder_slip_context",
			{ p_invoice_ids: invoiceIds }
		);
		if (error) {
			notifyError(error, "Error fetching payment reminder slip context");
			return null;
		}
		return data ?? [];
	},

	fetchFeeInvoiceReminder: async (feeInvoiceId) => {
		const { data: reminder, error } = await supabase
			.from("payment_reminders")
			.select("reminder_stage, invoice_id")
			.eq("fee_invoice_id", feeInvoiceId)
			.is("voided_at", null)
			.maybeSingle();
		if (error) {
			notifyError(error, "Error fetching fee invoice reminder");
			return null;
		}
		if (!reminder) return null;
		const { count, error: countError } = await supabase
			.from("payment_reminders")
			.select("id", { count: "exact", head: true })
			.eq("invoice_id", reminder.invoice_id)
			.is("voided_at", null)
			.gt("reminder_stage", reminder.reminder_stage);
		if (countError) {
			notifyError(countError, "Error fetching higher reminder stages");
			return null;
		}
		return { stage: reminder.reminder_stage, isLatest: count === 0 };
	},

	fetchOpenReminderFees: async (invoiceId) => {
		const { data, error } = await supabase.rpc(
			"get_invoice_open_reminder_fees",
			{ p_invoice_id: invoiceId }
		);
		if (error) {
			notifyError(error, "Error fetching open reminder fees");
			return [];
		}
		return (data ?? []).filter(
			(fee) => !fee.has_payments && fee.open_amount > OPEN_EPSILON
		);
	},
});
